/**
 * Investor Payment Schedule Page
 * 
 * Shows upcoming, overdue, and paid payments across all portfolios.
 * Includes a summary of total expected vs received.
 */
import { useInvestorAuth } from "@/hooks/useInvestorAuth";
import InvestorLayout from "@/components/InvestorLayout";
import { trpc } from "@/lib/trpc";
import { Calendar, CheckCircle2, AlertCircle, Clock, DollarSign, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

const BRAND = {
  bg: "#080d16",
  card: "#0e1623",
  border: "#1a2540",
  accent: "#3b82f6",
  accentLight: "rgba(59,130,246,0.1)",
  gold: "#f59e0b",
  goldLight: "rgba(245,158,11,0.1)",
  green: "#10b981",
  greenLight: "rgba(16,185,129,0.1)",
  red: "#ef4444",
  redLight: "rgba(239,68,68,0.1)",
  text: "#f1f5f9",
  textMuted: "#94a3b8",
  textDim: "#64748b",
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; color: string; label: string }> = {
    pending: { bg: BRAND.goldLight, color: BRAND.gold, label: "Upcoming" },
    paid: { bg: BRAND.greenLight, color: BRAND.green, label: "Paid" },
    overdue: { bg: BRAND.redLight, color: BRAND.red, label: "Overdue" },
  };
  const c = config[status] || config.pending;
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {c.label}
    </span>
  );
}

function PaymentRow({ payment }: { payment: any }) {
  const amount = parseFloat(payment.amount || "0");
  const isPaid = payment.status === "paid";
  const isOverdue = payment.status === "overdue";

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr 1fr 1fr",
        alignItems: "center",
        padding: "14px 20px",
        borderBottom: `1px solid ${BRAND.border}`,
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = "rgba(255,255,255,0.02)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = "transparent";
      }}
    >
      <div>
        <div style={{ color: BRAND.text, fontSize: 13, fontWeight: 500 }}>
          {payment.portfolioName || `Portfolio #${payment.portfolio_id}`}
        </div>
        <div style={{ color: BRAND.textDim, fontSize: 12, marginTop: 2 }}>
          Payment #{payment.payment_number}
        </div>
      </div>
      <div style={{ color: BRAND.textMuted, fontSize: 13 }}>{formatDate(payment.due_date)}</div>
      <div
        style={{
          color: isPaid ? BRAND.green : isOverdue ? BRAND.red : BRAND.text,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {formatCurrency(amount)}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <StatusBadge status={payment.status} />
      </div>
    </div>
  );
}

function SectionTable({
  title,
  payments,
  icon: Icon,
  iconColor,
  defaultOpen = true,
}: {
  title: string;
  payments: any[];
  icon: React.ComponentType<{ size?: number; color?: string }>;
  iconColor: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const total = payments.reduce((sum, p) => sum + parseFloat(p.amount || "0"), 0);

  return (
    <div
      style={{
        background: BRAND.card,
        border: `1px solid ${BRAND.border}`,
        borderRadius: 14,
        overflow: "hidden",
        marginBottom: 20,
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          borderBottom: open ? `1px solid ${BRAND.border}` : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: iconColor + "18",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={15} color={iconColor} />
          </div>
          <span style={{ color: BRAND.text, fontSize: 14, fontWeight: 700 }}>{title}</span>
          <span
            style={{
              background: "rgba(255,255,255,0.06)",
              color: BRAND.textMuted,
              padding: "2px 8px",
              borderRadius: 20,
              fontSize: 12,
            }}
          >
            {payments.length}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ color: BRAND.textMuted, fontSize: 13, fontWeight: 600 }}>
            {formatCurrency(total)}
          </span>
          {open ? (
            <ChevronUp size={16} color={BRAND.textDim} />
          ) : (
            <ChevronDown size={16} color={BRAND.textDim} />
          )}
        </div>
      </button>

      {open && (
        <>
          {/* Table header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr",
              padding: "10px 20px",
              background: "rgba(255,255,255,0.02)",
              borderBottom: `1px solid ${BRAND.border}`,
            }}
          >
            {["Portfolio", "Due Date", "Amount", "Status"].map((h) => (
              <div
                key={h}
                style={{
                  color: BRAND.textDim,
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  textAlign: h === "Status" ? "right" : "left",
                }}
              >
                {h}
              </div>
            ))}
          </div>

          {payments.length === 0 ? (
            <div style={{ padding: "24px 20px", color: BRAND.textDim, fontSize: 13, textAlign: "center" }}>
              No payments in this category.
            </div>
          ) : (
            payments.map((p: any) => <PaymentRow key={`${p.portfolio_id}-${p.payment_number}`} payment={p} />)
          )}
        </>
      )}
    </div>
  );
}

export default function InvestorSchedule() {
  const { user, loading: authLoading } = useInvestorAuth({ redirectOnUnauthenticated: true });

  const scheduleQuery = trpc.investor.getPaymentSchedule.useQuery(
    { investorId: user?.id ?? 0 },
    { enabled: !!user?.id }
  );

  const schedule = scheduleQuery.data;

  const totalPending = schedule?.upcoming.reduce(
    (sum: number, p: any) => sum + parseFloat(p.amount || "0"),
    0
  ) ?? 0;
  const totalPaid = schedule?.paid.reduce(
    (sum: number, p: any) => sum + parseFloat(p.amount || "0"),
    0
  ) ?? 0;
  const totalOverdue = schedule?.overdue.reduce(
    (sum: number, p: any) => sum + parseFloat(p.amount || "0"),
    0
  ) ?? 0;

  return (
    <InvestorLayout>
      <div style={{ padding: "32px 36px", maxWidth: 1100 }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ color: BRAND.text, fontSize: 24, fontWeight: 700, margin: "0 0 6px 0" }}>
            Payment Schedule
          </h1>
          <p style={{ color: BRAND.textMuted, fontSize: 14, margin: 0 }}>
            Track upcoming, received, and overdue payments across all your portfolios.
          </p>
        </div>

        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 28,
          }}
        >
          {[
            {
              label: "Upcoming Payments",
              value: formatCurrency(totalPending),
              count: schedule?.upcoming.length ?? 0,
              color: BRAND.gold,
              colorLight: BRAND.goldLight,
              icon: Clock,
            },
            {
              label: "Total Received",
              value: formatCurrency(totalPaid),
              count: schedule?.paid.length ?? 0,
              color: BRAND.green,
              colorLight: BRAND.greenLight,
              icon: CheckCircle2,
            },
            {
              label: "Overdue",
              value: formatCurrency(totalOverdue),
              count: schedule?.overdue.length ?? 0,
              color: BRAND.red,
              colorLight: BRAND.redLight,
              icon: AlertCircle,
            },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                background: BRAND.card,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 12,
                padding: "16px 18px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: item.colorLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <item.icon size={14} color={item.color} />
                </div>
                <span style={{ color: BRAND.textMuted, fontSize: 12 }}>{item.label}</span>
              </div>
              <div style={{ color: BRAND.text, fontSize: 20, fontWeight: 700, marginBottom: 2 }}>
                {item.value}
              </div>
              <div style={{ color: BRAND.textDim, fontSize: 12 }}>{item.count} payments</div>
            </div>
          ))}
        </div>

        {/* Payment Tables */}
        {scheduleQuery.isLoading ? (
          <div style={{ color: BRAND.textMuted, fontSize: 14, padding: "20px 0" }}>
            Loading payment schedule...
          </div>
        ) : (
          <>
            {(schedule?.overdue.length ?? 0) > 0 && (
              <SectionTable
                title="Overdue Payments"
                payments={schedule?.overdue ?? []}
                icon={AlertCircle}
                iconColor={BRAND.red}
                defaultOpen={true}
              />
            )}
            <SectionTable
              title="Upcoming Payments"
              payments={schedule?.upcoming ?? []}
              icon={Calendar}
              iconColor={BRAND.gold}
              defaultOpen={true}
            />
            <SectionTable
              title="Received Payments"
              payments={schedule?.paid ?? []}
              icon={CheckCircle2}
              iconColor={BRAND.green}
              defaultOpen={false}
            />
          </>
        )}
      </div>
    </InvestorLayout>
  );
}
